// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Mutation hook for Dismiss: record the operator's acceptance of a
 * single field's divergence without changing the Hardfin value. Only
 * the discrepancies query and the entity-detail query need refresh —
 * the rest of the cache is unaffected.
 */
import { dismissSFDCDriftField } from '@/sfdc/api/drift';
import {
  SFDCDriftEntityType,
  SFDCDriftResolutionResponse,
} from '@/sfdc/api/types';
import {
  sfdcDriftDiffQueryKey,
  sfdcDriftEntityDetailQueryKey,
} from '@/sfdc/hooks/query-keys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface Variables {
  fieldName: string;
}

export function useDismissSFDCDriftField(
  entityType: SFDCDriftEntityType,
  entityKey: string
) {
  const queryClient = useQueryClient();

  return useMutation<SFDCDriftResolutionResponse, AxiosError, Variables>({
    mutationFn: ({ fieldName }) =>
      dismissSFDCDriftField(entityType, entityKey, fieldName),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: sfdcDriftDiffQueryKey(entityType, entityKey),
      });
      void queryClient.invalidateQueries({
        queryKey: sfdcDriftEntityDetailQueryKey(entityType, entityKey),
      });
    },
  });
}
